export type EventSummary = {
  id: string;
  code: string | null;
  name: string;
  status: string | null;
  startAt: string | null;
  endAt: string | null;
  clientId: string | null;
  locationId: string | null;
};

export type EventDetail = EventSummary & {
  stage: string | null;
  description: string | null;
  venueId?: string | null;
  metadata?: Record<string, unknown> | null;
};

const asString = (value: unknown): string | null => {
  if (value == null) return null;
  const str = String(value).trim();
  return str.length ? str : null;
};

export const rowToEventSummary = (row: any): EventSummary => {
  const id = asString(row?.event_id ?? row?.id) ?? '';
  return {
    id,
    code: asString(row?.event_code ?? row?.code),
    name: asString(row?.name) ?? '',
    status: asString(row?.status ?? row?.stage),
    startAt: asString(
      row?.start_at ??
        row?.startAt ??
        row?.start_date ??
        row?.event_start_date ??
        row?.show_start_date ??
        row?.event_date,
    ),
    endAt: asString(
      row?.end_at ??
        row?.endAt ??
        row?.end_date ??
        row?.event_end_date ??
        row?.show_end_date,
    ),
    clientId: asString(row?.client_id ?? row?.clientId),
    locationId: asString(row?.location_id ?? row?.site_location_id ?? row?.locationId),
  };
};

export const rowToEventDetail = (row: any): EventDetail => {
  const summary = rowToEventSummary(row);
  const metadata = row?.metadata && typeof row.metadata === 'object' ? row.metadata : null;
  return {
    ...summary,
    stage: asString(row?.stage ?? summary.status),
    description: asString(row?.description ?? row?.notes),
    venueId: asString(row?.venue_id ?? row?.event_venue_id ?? row?.location_id ?? null),
    metadata,
  };
};

export const eventDetailToUpsertInput = (detail: EventDetail): Record<string, unknown> => ({
  id: detail.id,
  event_code: detail.code ?? null,
  name: detail.name.trim(),
  status: detail.status ?? null,
  event_start_date: detail.startAt ?? null,
  event_end_date: detail.endAt ?? null,
  client_id: detail.clientId ?? null,
  notes: detail.description ?? null,
  site_location_id: detail.locationId ?? null,
});

export const validateEventCode = (code: unknown): string | null => {
  const value = typeof code === 'string' ? code.trim() : '';
  if (!value) return 'Event code is required.';
  if (/\s/.test(value)) return 'Event code cannot contain spaces.';
  return null;
};

export const validateEventName = (name: unknown): string | null => {
  const value = typeof name === 'string' ? name.trim() : '';
  if (!value) return 'Event name is required.';
  if (value.length < 3) return 'Event name must be at least 3 characters.';
  return null;
};

export {
  APPROVAL_INERT_EVENT_STATUSES,
  TERMINAL_EVENT_STATUSES,
  isApprovalInertEventStatus,
  isTerminalEventStatus,
  terminalEventStatusLabel,
  type ApprovalInertEventStatus,
  type TerminalEventStatus,
} from './eventStatus';

export {
  eventStageButtonActions,
  eventStageButtonModel,
  type EventStageAction,
  type EventStageActionId,
  type EventStageButtonInput,
  type EventStageButtonModel,
} from './stageActions';

export {
  createEventClientFileDownloadUrl,
  staffEventClientFilesQueryKey,
  useStaffEventClientFiles,
  type StaffEventClientFile,
} from './useEventClientFiles';
export { eventDisplayName } from './eventDisplayName';
